import Head from 'next/head';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';

import { withPriceSeperator } from '@flight-workspace/common-client/src/util/number';
import { getCityImageUrl } from 'common/cityImage';
import {
  getStayLength,
  getTripInfo,
  parseRoutes,
  stringifyDepartureDate,
} from 'common/utils';
import { initApolloClient } from 'config/apollo';
import config from 'config/index';
import { tripTypes } from 'contexts/searchParams';
import {
  getAirportList,
  getAirportsInCity,
  getSummarizedLocationCodes,
} from 'hooks/useAirportDetailList';
import { getMinPrice } from './util';

interface IProps {
  description?: string;
  image?: string;
  title?: string;
}

const DEPARTURE_INDEX = 0;
const ARRIVAL_INDEX = 1;

export const getMetadataForSEO = async (
  query: ParsedUrlQuery,
): Promise<{ description?: string; image?: string; title?: string }> => {
  const { departureCity: departureIataCode, arrivalCity: arrivalIataCode } =
    query;

  if (
    typeof departureIataCode !== 'string' ||
    typeof arrivalIataCode !== 'string' ||
    !departureIataCode ||
    !arrivalIataCode ||
    departureIataCode !== 'SEL' // SEO 1단계: 출발지 서울 고정
  )
    return {};

  const apolloClient = initApolloClient();

  const itineraryIataDetails = await getAirportList(apolloClient, [
    departureIataCode,
    arrivalIataCode,
  ]);

  // 출/도착지가 유효한 IATA 코드(공항/도시)가 아닌 경우
  if (
    !(
      itineraryIataDetails[DEPARTURE_INDEX]?.cityName &&
      itineraryIataDetails[ARRIVAL_INDEX]?.cityName
    )
  )
    return {};

  const {
    airportName: arrivalAirportName,
    cityName: arrivalCityName,
    cityImage: arrivalCityImage,
    countryName: arrivalCountryName,
  } = itineraryIataDetails[ARRIVAL_INDEX];

  const isArrivalAirport = !!itineraryIataDetails[ARRIVAL_INDEX]?.airportName;

  const arrivalAirportDetails = await getAirportsInCity({
    apolloClient,
    cityCode: arrivalIataCode,
    isSkip: isArrivalAirport,
  });
  const minPrice = await getMinPrice({
    apolloClient,
    scity: departureIataCode,
    ecity: isArrivalAirport
      ? arrivalIataCode
      : arrivalAirportDetails.map(airport => airport.iataCode).join(','),
  });

  const minPriceString = minPrice ? withPriceSeperator(minPrice) : '';

  const arrivalName = arrivalAirportName || arrivalCityName;

  const title = `${
    arrivalCountryName !== arrivalName ? `${arrivalCountryName} ` : ''
  }${arrivalName}(${arrivalIataCode}) 항공권 실시간 특가${
    minPriceString ? `(${minPriceString}원)` : ''
  } 확인하기 | 네이버항공권`;

  const description = `${arrivalName} 항공권을 최저가${
    minPriceString ? ` ${minPriceString}원부터 실시간` : '로'
  } 비교하여 구매해 보세요. 신용카드 별 추가 할인부터 땡처리 요금까지 한번에 찾아 드려요.`;

  const image =
    arrivalCityImage?.imagePath &&
    getCityImageUrl(
      'f1200_630',
      arrivalCityImage.imagePath,
      `${config.flight}/images/og_image.png`,
    );

  return {
    title,
    description,
    image,
  };
};

export async function getMetadataFromSearchParams({
  routes,
  adult,
  child,
  infant,
}: ParsedUrlQuery) {
  const client = initApolloClient();
  const itineraries = parseRoutes(routes as string[]);
  const locationCodes = getSummarizedLocationCodes(itineraries);
  const airportDetailList = await getAirportList(client, locationCodes);
  const [, firstDestination] = airportDetailList;
  const airportMap = new Map(
    airportDetailList.map(airport => [airport.iataCode, airport]),
  );
  const locations = locationCodes
    .map(code => airportMap.get(code)?.cityName || code)
    .join('-');
  const tripType = getTripInfo(itineraries);
  const stayLength = getStayLength(itineraries);
  const departureDates = itineraries
    .map(stringifyDepartureDate(tripType, stayLength))
    .join(' ~ ');
  const tripTypeName = tripTypes.get(tripType);
  const passengers = `${(+(adult as any) || 0) + (+(child as any) || 0) + (+(infant as any) || 0)}명`;
  return {
    description: [locations, departureDates, tripTypeName, passengers].join(
      ', ',
    ),
    image:
      firstDestination?.cityImage?.imagePath &&
      getCityImageUrl('f1200_630', firstDestination.cityImage.imagePath),
  };
}

export function MetaHead({
  description = '설레는 여행의 시작. 네이버 항공권과 함께!',
  image = `${config.flight}/images/og_image.png`,
  title = '네이버 항공권',
}: IProps) {
  const { asPath } = useRouter();
  return (
    <Head>
      <meta
        name="viewport"
        content="width=device-width,initial-scale=1.0,maximum-scale=2.0,user-scalable=yes"
      />
      <title>{title}</title>
      <meta name="application-name" content="flight" />
      <meta name="description" content={description} />
      <meta name="image" content={image} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <link rel="canonical" href={`${config.flight}${asPath}`} />
      <link rel="icon" href={`${config.flight}/images/favicon/flight.ico`} />
      <link
        rel="icon"
        href={`${config.flight}/images/favicon/android-192x192.png`}
        sizes="192x192"
      />
      <link
        rel="apple-touch-icon"
        href={`${config.flight}/images/favicon/ios-180x180.png`}
        sizes="180x180"
      />
    </Head>
  );
}

export function MyMetaHead() {
  const title = '네이버 여행MY';
  const image =
    'https://vertical-travel.pstatic.net/vertical/static/travelhome/icon.png';
  return (
    <Head>
      <title>{title}</title>
      <meta property="og:title" content={title} />
      <meta property="og:image" content={image} />
      <link
        rel="icon"
        href={`${config.flightCdn.url}/flight/assets/vertical/favicons/my.png`}
      />
      <link
        rel="apple-touch-icon"
        href={`${config.flightCdn.url}/flight/assets/vertical/favicons/my-apple-icon-180x180.png`}
        sizes="180x180"
      />
    </Head>
  );
}
